/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC } from 'react';
import {
  TextTitle,
  Spacer,
  TextCaption,
  PageSection,
  Filepond,
  EntityTypes,
  SetProfilePicture,
  Stack,
  Tooltip,
  Icon,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { throwErrorWithId } from '@pypestream/utils';

import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { StudioPageProps, useProStudioViewerRole } from '../hooks';
import { WithLoadingState } from '../router';
import { withStageNavRedirect } from './redirect-into-stage-nav';
import { AssetCard } from '../components';
import { uploadFile } from '../utils';

import './files.scss';

type FilesPageProps = StudioPageProps;

type Asset = {
  id: string;
  url: string;
};

export const FilesContent: FC<FilesPageProps> = () => {
  const [t] = useTranslation();
  const { assets, botId, projectId } = useProStudioCtxSelector((ctx) => {
    const ctxAssets: Asset[] = ((ctx.selectedBot?.assets || []) as Asset[])[0]
      ?.id
      ? ((ctx.selectedBot?.assets || []) as Asset[])
      : [];

    const { customerId, botName, project } = ctx;

    return {
      botId: customerId && botName ? `${customerId}.${botName}` : undefined,
      assets: ctxAssets,
      projectId: project?.id,
    };
  });

  const isViewer = useProStudioViewerRole();

  const onUpload: SetProfilePicture = ({
    entityId,
    file,
    load,
    error,
    progress,
    abort,
    onChange,
  }) => {
    if (!projectId || !entityId) {
      throwErrorWithId('Project ID and bot ID are required');
    }

    const uploadUrl = `${
      import.meta.env.FE_ADMIN_URL
    }/rest/bot-manager/${projectId}/${entityId}/assets`;

    return uploadFile({
      url: uploadUrl,
      file: file as File,
      type: 'assetData',
      load,
      progress,
      abort,
      onChange,
      error: (e) => {
        sendProStudioEvent({
          type: 'showError',
          error: e,
        });
        error?.(e);
      },
    });
  };

  return (
    <>
      <Stack gutter="xsmall" alignItems="center">
        <TextTitle test-id="ps-files-page-title" size="small">
          <TranslationComponent i18nKey="pro-studio/files:title">
            Media and Files
          </TranslationComponent>
        </TextTitle>
        <Tooltip test-id="ps-files-page-info-tooltip" placement="right">
          <Icon
            test-id="ps-files-page-info-icon"
            name="info"
            color="gray"
            size="small"
          />
          <TextCaption variant="primary" slot="content">
            <TranslationComponent i18nKey="pro-studio/files:description.part1">
              In order for your solution to display images, documents, or videos
              to the user, these assets must first be uploaded here. The
              asset&apos;s URL can then be included within the Workflows tab.
              For more information regarding acceptable file formats and
              restrictions on file size, please refer to
            </TranslationComponent>{' '}
            <a href="#">
              <TranslationComponent i18nKey="pro-studio/files:description.part2">
                Designing Agents
              </TranslationComponent>
            </a>
            .
          </TextCaption>
        </Tooltip>
      </Stack>
      <Spacer size="large" />
      <PageSection test-id="ps-files-page-section" size="small">
        <div className="c-files-page__list">
          {!isViewer && (
            <>
              <Filepond
                test-id="ps-files-page-filepond"
                vertical
                allowMultiple
                className="c-files-page__list-item"
                entityType={EntityTypes.ASSET}
                entityId={botId}
                previewLabel={
                  t('pro-studio/files:filepond.previewLabel', {
                    defaultValue: 'Drag a file here',
                  }) || ''
                }
                onUpload={onUpload}
                onChange={(props) => {
                  if (!props?.data) {
                    return;
                  }

                  sendProStudioEvent({
                    type: 'updateAssets',
                    // @ts-ignore
                    data: props.data,
                  });
                }}
              />
            </>
          )}

          {assets.map(({ id, url }) => (
            <AssetCard
              testId={`ps-files-page-asset-card-${id}`}
              key={id}
              id={id}
              url={url}
              viewOnly={isViewer}
            />
          ))}
        </div>
      </PageSection>
    </>
  );
};

export const Files = withStageNavRedirect(WithLoadingState(FilesContent), {
  stage: 'build',
  buildViewMode: 'files',
});
